/* eslint-disable i18next/no-literal-string */

/* istanbul ignore file */
import {useEffect, useState} from 'react';

import {useWolfTheme} from '../contexts';

/**
 *
 * When testing specific breakpoints in tests you can use the TEST_BREAKPOINT environment variable to overwrite the behaviour of these hooks
 * in node example
 *
 * `process.env.TEST_BREAKPOINT = 'sm'`
 *
 * REMEMBER to reset the breakpoint afterwards or we may have wierd behaviour in tests.
 *
 * `delete process.env.TEST_BREAKPOINT`
 *
 */

const breakpointOrder: Array<keyof Breakpoints> = ['sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'];

const isBreakpointActive = (b: keyof Breakpoints): boolean => {
    const breakpoint = process.env.TEST_BREAKPOINT || 'xxxl';
    return breakpointOrder.findIndex(val => val === breakpoint) >= breakpointOrder.findIndex(val => val === b);
};

export type Breakpoints = {
    sm: string;
    md: string;
    lg: string;
    xl: string;
    xxl: string;
    xxxl: string;
};

/* eslint-disable i18next/no-literal-string */
export const useBreakpoints = (): Breakpoints => {
    const theme = useWolfTheme();

    // Jest doesn't support media queries and strips out the styles
    // so when in jest there is a global variable on the window object called 'activeBreakpoint' which can be set from any jest test.
    // If running in jest this hook will respect that global variable instead of using match media.
    if (typeof process !== 'undefined' && process.env.JEST_WORKER_ID !== undefined) {
        const includeBreakpoint = (b: keyof Breakpoints) => {
            if (isBreakpointActive(b)) {
                // In sass & just means including self, so if breakpoint is less than what we say it should be we include styles.
                return '&';
            }
            // Else we ignore them using media queires, jest strips all media queries from css.
            return '@media (min-width: 10000px)';
        };

        return {
            sm: includeBreakpoint('sm'),
            md: includeBreakpoint('md'),
            lg: includeBreakpoint('lg'),
            xl: includeBreakpoint('xl'),
            xxl: includeBreakpoint('xxl'),
            xxxl: includeBreakpoint('xxxl'),
        };
    }

    return {
        sm: '@media (min-width: ' + theme.layout.smMax + ')',
        md: `@media (min-width: ${theme.layout.mdMax})`,
        lg: `@media (min-width: ${theme.layout.lgMax})`,
        xl: `@media (min-width: ${theme.layout.xlMin})`,
        xxl: `@media (min-width: 1680px)`, // May need combining with the theme.
        xxxl: `@media (min-width: 2000px)`, // May need combining with the theme.
    };
};

// Testing this is pretty pointless in jest, it doesn't respect media queries.
export const useMediaQuery = (size: keyof Breakpoints) => {
    const bp = useBreakpoints();
    const [matches, setMatches] = useState(() => {
        /* istanbul ignore next */
        if (typeof process !== 'undefined' && process.env.JEST_WORKER_ID !== undefined) {
            if (isBreakpointActive(size)) {
                return true;
            }
            return false;
        }
        return window.matchMedia(bp[size].replace('@media ', '')).matches;
    });

    // Jest doesn't support match media, so when in jest there is a global variable on the window object called 'activeBreakpoint' which can be set from any
    // jest test. If running in jest this hook will respect that global variable instead of using match media.
    /* istanbul ignore next */
    if (typeof process !== 'undefined') {
        return matches;
    }

    // Normally this is a terrible idea but because of the use case of the conditional, to replace the functionality of this hook in jest
    // this is fine, though we will need to be careful making changes if this hook becomes more complex.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        const matchMedia = window.matchMedia(bp[size].replace('@media ', ''));

        const handleChange = (ev: MediaQueryListEvent) => {
            setMatches(ev.matches);
        };

        matchMedia.addEventListener('change', handleChange);

        return () => {
            setMatches(matchMedia.matches);
            matchMedia.removeEventListener('change', handleChange);
        };
    }, [bp, size]);

    return matches;
};
